import { AvlTable } from '../../../../Shared/Table/Table';
import { useEffect, useMemo, useState } from 'react';
import SkeletonComponent from '../../../../SkeletonComponent';
import { AvlCheckbox } from '../../../../Shared/Inputs/Checkbox/AvlCheckbox';
import styles from './MissingInvoicesTable.module.css';
import { dateToFormattedString, formatNumberAsCurrency } from '../../../../../Utils/Utils';
import { ShowMore } from '../../../../Shared/ShowMore/ShowMore';

export const FieldKeys = {
    purchaseId: 'purchaseId',
    transactionId: 'transactionId',
    messageId: 'messageId',
    reference: 'reference',
    userName: 'userName',
    organisation: 'organisation',
    isTrial: 'isTrial',
    fee: 'fee',
    orderedAt: 'orderedAt',
    lastExpectedInvoiceDate: 'lastExpectedInvoiceDate',
    oType: 'oType',
    folderId: 'folderId',
    documentId: 'documentId',
    message: 'message',

    // Group-specific fields
    lastOrderedAt: 'lastOrderedAt',
    allTypes: 'allTypes',
};

const Columns = [
    {
        key: FieldKeys.purchaseId,
        label: 'Purchase ID',
        align: 'left',
        getCellData: (row) => ({ data: row[FieldKeys.purchaseId] || 'N/A' }),
    },
    {
        key: FieldKeys.transactionId,
        label: 'Transaction ID',
        align: 'left',
        getCellData: (row) => ({ data: row[FieldKeys.transactionId] || 'N/A' }),
    },
    {
        key: FieldKeys.messageId,
        label: 'Message ID',
        align: 'left',
        getCellData: (row) => ({ data: row[FieldKeys.messageId] || 'N/A' }),
    },
    {
        key: FieldKeys.folderId,
        label: 'Folder ID',
        align: 'left',
        getCellData: (row) => ({ data: row[FieldKeys.folderId] || 'N/A' }),
    },
    {
        key: FieldKeys.documentId,
        label: 'Document ID',
        align: 'left',
        getCellData: (row) => ({ data: row[FieldKeys.documentId] || 'N/A' }),
    },
    {
        key: FieldKeys.organisation,
        label: 'Organisation',
        align: 'left',
        getCellData: (row) => ({ data: row[FieldKeys.organisation] || 'N/A' }),
    },
    {
        key: FieldKeys.isTrial,
        label: 'Is Trial',
        align: 'left',
        getCellData: (row) => ({
            style: { textAlign: 'center' },
            data: (
                <AvlCheckbox
                    isDisabled
                    value={row[FieldKeys.isTrial]}
                />
            ),
        }),
    },
    {
        key: FieldKeys.reference,
        label: 'Reference',
        align: 'left',
        getCellData: (row) => ({ data: row[FieldKeys.reference] || 'N/A' }),
    },
    {
        key: FieldKeys.userName,
        label: 'User',
        align: 'left',
        getCellData: (row) => ({ data: row[FieldKeys.userName] || 'N/A' }),
    },
    {
        key: FieldKeys.fee,
        label: 'Fee',
        align: 'center',
        getCellData: (row) => ({
            style: { textAlign: 'center' },
            data: formatNumberAsCurrency(row[FieldKeys.fee]) || 'N/A',
        }),
    },
    {
        key: FieldKeys.orderedAt,
        label: 'Ordered At',
        align: 'left',
        getCellData: (row) => ({
            style: { minWidth: '90px' },
            data: row[FieldKeys.orderedAt] || 'N/A',
        }),
    },
    {
        key: FieldKeys.lastExpectedInvoiceDate,
        label: 'Last Expected Invoice Date',
        align: 'left',
        getCellData: (row) => ({
            style: { minWidth: '180px' },
            data: row[FieldKeys.lastExpectedInvoiceDate] || 'N/A',
        }),
    },
    {
        key: FieldKeys.oType,
        label: 'OType',
        align: 'left',
        getCellData: (row) => ({
            style: { minWidth: '80px' },
            data: row[FieldKeys.oType] || 'N/A',
        }),
    },
    {
        key: FieldKeys.message,
        label: 'Message',
        align: 'left',
        getCellData: (row) => ({ data: row[FieldKeys.message] || 'N/A' }),
    },

    // Group-specific columns
    {
        key: FieldKeys.allTypes,
        label: 'Types',
        align: 'left',
        getCellData: (row) => ({
            data: row[FieldKeys.allTypes]?.length
                ? (
                    <ShowMore collapsedSize={60}>
                        {
                            row[FieldKeys.allTypes].map((type, index) => (
                                <div key={index}>
                                    <span>{type}</span>
                                </div>
                            ))
                        }
                    </ShowMore>
                )
                : 'N/A'
        }),
    },
    {
        key: FieldKeys.lastOrderedAt,
        label: 'Last Ordered At',
        align: 'left',
        getCellData: (row) => ({ data: dateToFormattedString(row[FieldKeys.lastOrderedAt], 'N/A') }),
    },
];

export function MissingInvoicesTable({ isLoading, invoices, customHiddenColumns = [] }) {
    const [countRowsPerPage, setCountRowsPerPage] = useState(20);
    const pageCount = useMemo(() => {
        return Math.ceil(invoices.length / countRowsPerPage);
    }, [invoices, countRowsPerPage]);
    const [currentPage, setCurrentPage] = useState(1);
    const [visibleTableRows, setVisibleRows] = useState([]);

    const visibleColumns = useMemo(() => {
        return Columns.filter((column) => !customHiddenColumns.includes(column.key));
    }, [customHiddenColumns]);

    const tableHeaders = useMemo(() => {
        return visibleColumns.map((column) => ({
            label: column.label,
            align: column.align,
        }));
    }, [visibleColumns]);

    const convertToCells = (row, rowIndex) => {
        return visibleColumns.map((column) => column.getCellData(row));
    };

    const showRows = (page, countRowsPerPage, invoices) => {
        const firstPageRowIndex = (page - 1) * countRowsPerPage;
        const pageInvoices = invoices.slice(firstPageRowIndex, firstPageRowIndex + countRowsPerPage);
        const tableRows = pageInvoices.map((row, rowIndex) => ({
            cells: convertToCells(row, rowIndex),
        }));

        setVisibleRows(tableRows);
    };

    useEffect(() => {
        showRows(currentPage, countRowsPerPage, invoices);
    }, [invoices.length, visibleColumns, currentPage, countRowsPerPage]);

    const handleChangePage = (event, page) => {
        setCurrentPage(page);
        showRows(page, countRowsPerPage, invoices);
    };

    const handleChangeRowsPerPagePage = (rowsPerPage) => {
        const initialPage = 1;
        setCountRowsPerPage(rowsPerPage);
        setCurrentPage(initialPage);
        showRows(initialPage, rowsPerPage, invoices);
    };

    const content = invoices?.length
        ? (
            <AvlTable
                headers={tableHeaders}
                rows={visibleTableRows}
                page={currentPage}
                onPageChange={handleChangePage}
                pageCount={pageCount}
                isRowsPerPageOptionsVisible
                rowsPerPage={countRowsPerPage}
                rowsCount={invoices.length}
                onChangeRowsPerPage={handleChangeRowsPerPagePage}
            />
        ) : (
            <div className={styles.noContentMessage}>
                Missing invoices not found
            </div>
        );

    return isLoading
        ? (
            <SkeletonComponent />
        )
        : content;
}
